import type { ObservableSubscription } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';

import { IResponse } from '@work4all/data/lib/hooks/data-provider/useDataProvider';
import { useCustomerOnly } from '@work4all/data/lib/hooks/useCustomerOnly';

import { DISABLED_CUSTOMER_FOR_TICKET_COUNTS } from '../../../../../config';

import { getKindFiltersCountsQuery } from './get-kind-filters-counts-query';

type Props = {
  ids: number[];
};

const FOLLOW_UP_DATE = DateTime.now().endOf('day').toISO();

const EMPTY_ARRAY = [];

export const useKindFiltersCounts = ({ ids = EMPTY_ARRAY }: Props) => {
  const [result, setResult] = useState<Map<number, number>>(new Map());
  const subscriptionRef = useRef<ObservableSubscription | null>(null);

  const client = useApolloClient();

  const query = useMemo(() => {
    return getKindFiltersCountsQuery({ ids, followUpDate: FOLLOW_UP_DATE });
  }, [ids]);

  const disabledForTicketCounts = useCustomerOnly(
    DISABLED_CUSTOMER_FOR_TICKET_COUNTS
  );

  useEffect(() => {
    if (!query || disabledForTicketCounts) {
      return;
    }

    const response = client.watchQuery<Record<string, IResponse>>({
      query,
      context: { batch: false },
    });

    subscriptionRef.current = response.subscribe((grouped) => {
      const newResult = new Map();

      if (grouped.loading) {
        return;
      }

      if (grouped.error || !grouped.data) {
        setResult(newResult);
        return;
      }

      for (const [key, value] of Object.entries(grouped.data)) {
        newResult.set(Number(key.split('_')[1]), value?.total ?? 0);
      }

      setResult(newResult);
    });

    return () => {
      subscriptionRef.current?.unsubscribe();
    };
  }, [query, client, disabledForTicketCounts]);

  return result;
};
